/**
 * The one element every pressable thing in anubis renders through.
 *
 * - `href` → an `<a>`. Another origin, a `mailto:`, a legal page. `external` opens a new tab with
 *   `rel="noopener noreferrer"` — never for `mailto:`, which a browser hands to the mail client.
 * - `to` → the app's router link (see `Anubis`), or an `<a href>` when the app has no router.
 * - neither → a `<button>`, `type="button"` unless told otherwise, so a press inside a `Form` never
 *   submits by accident.
 *
 * ⚠️ A navigation is an ANCHOR, never a button with a handler. `store`'s calls to action were buttons
 * until 2026-09-24, and the product page shipped with no link to the checkout anywhere in it — which
 * is what a crawler, a middle click and "open in new tab" saw. `onClick` here is for reporting only
 * and never prevents the navigation.
 */
import type { ReactNode } from 'react';

import { useLinkComponent } from './Anubis';

export interface PressableProps {
  href?: string | undefined;
  to?: string | undefined;
  external?: boolean | undefined;
  type?: 'button' | 'submit' | undefined;
  disabled?: boolean | undefined;
  pressed?: boolean | undefined;
  current?: boolean | undefined;
  onClick?: (() => void) | undefined;
  label?: string | undefined;
  hidden?: boolean | undefined;
  /** Data attributes an app's analytics listener reads (`links`' outbound-click tracker). */
  data?: Readonly<Record<string, string>> | undefined;
  className: string;
  children?: ReactNode | undefined;
}

function dataAttributes(data: PressableProps['data']): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(data ?? {})) out[`data-${key}`] = value;
  return out;
}

export function Pressable({
  href,
  to,
  external,
  type = 'button',
  disabled,
  pressed,
  current,
  onClick,
  label,
  hidden,
  data,
  className,
  children,
}: PressableProps) {
  const Link = useLinkComponent();
  const shared = {
    className,
    ...(label ? { 'aria-label': label } : {}),
    ...(hidden ? { 'aria-hidden': true, tabIndex: -1 } : {}),
    ...(onClick ? { onClick } : {}),
  };

  if (to !== undefined && Link) {
    return (
      <Link to={to} {...shared}>
        {children}
      </Link>
    );
  }
  const target = href ?? to;
  if (target !== undefined) {
    const newTab = external && !target.toLowerCase().startsWith('mailto:');
    return (
      <a
        href={target}
        {...shared}
        {...dataAttributes(data)}
        {...(current ? { 'aria-current': 'page' as const } : {})}
        {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type={type}
      disabled={disabled}
      {...shared}
      {...dataAttributes(data)}
      {...(pressed !== undefined ? { 'aria-pressed': pressed } : {})}
      {...(current ? { 'aria-current': 'true' as const } : {})}
    >
      {children}
    </button>
  );
}
